/**
 * @file Public landing page — pages/Landing/CookiesPolicyPage.tsx.
 */

import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./LandingPage.css";
import PublicPageContent from "./components/PublicPageContent";
import type { PublicPageSection } from "../../services/publicPageService";
import "./components/PublicStaticPage.css";

const CookiesPolicyPage: React.FC = () => {
  const [pageTitle, setPageTitle] = useState("Cookies Policy");
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);

  const fallbackSections: PublicPageSection[] = useMemo(
    () => [
      {
        body: "We use cookies to ensure you get the best experience on our site.",
      },
      {
        heading: "What are cookies?",
        body: "Small text files stored on your device to remember your preferences.",
      },
      {
        heading: "How we use them",
        body: "We use Essential cookies (to keep the site running), and Analytical cookies (to see how people use the site).",
      },
      {
        heading: "Management",
        body: "You can block or delete cookies via your browser settings. Note that disabling cookies may limit your ability to use certain features of this website.",
      },
    ],
    []
  );

  return (
    <div className="landing-root">
      <header className="landing-header flex justify-center items-center space-x-3 text-center py-4">
        <h1 className="landing-main-title text-4xl font-bold text-primary">
          ICoMEC
        </h1>
        <h2 className="landing-sub-title text-xl text-grayDark">
          Indian Construction Materials Embodied Carbon Data Base
        </h2>
      </header>

      <nav className="landing-nav">
        <Link to="/">Home</Link>
        <Link to="/join">Join</Link>
        <Link to="/impact">Impact</Link>
        <Link to="/research">Research and Analysis</Link>
        <Link to="/about">About</Link>
      </nav>

      <section className="public-static-section">
        <div className="public-static-card">
          <div className="public-static-header">
            <div>
              <h1 className="public-static-title">{pageTitle}</h1>
              <p className="public-static-subtitle">
                Information about how cookies are used on this website.
              </p>
            </div>
            {updatedAt && (
              <div className="public-static-meta">
                Last updated: {new Date(updatedAt).toLocaleDateString()}
              </div>
            )}
          </div>
          <hr className="public-static-divider" />

          <PublicPageContent
            slug="cookies-policy"
            fallbackSections={fallbackSections}
            onMeta={({ title, updatedAt }) => {
              if (title) setPageTitle(title);
              if (updatedAt) setUpdatedAt(updatedAt);
            }}
          />
        </div>
      </section>

      <footer className="landing-footer">
        <div className="footer-main">
          <div className="footer-left">
            <h2>ICoMEC</h2>
            <p>
              Indian Construction Materials Embodied
              <br /> Carbon Data Base
            </p>
          </div>

          <div className="footer-links">
            <div>
              <h4>About Us</h4>
              <ul>
                <li><Link to="/who-we-are">Who We Are</Link></li>
                <li><Link to="/what-we-do">What We Do</Link></li>
                <li><Link to="/leadership">Leadership</Link></li>
                <li><Link to="/partners">Partners</Link></li>
                <li><Link to="/faq">FAQ</Link></li>
                <li><Link to="/glossary">Glossary</Link></li>
              </ul>
            </div>
            <div>
              <h4>Resources</h4>
              <ul>
                {/* <li><Link to="/resource-library">Resource Library</Link></li> */}
                <li><Link to="/events">Events</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>

      <div className="footer-bottom">
        <p>
          <Link to="/cookies-policy">Cookies Policy</Link> · <Link to="/privacy-policy">Privacy Policy</Link> · <Link to="/legal">Legal</Link> · <Link to="/terms-of-use">Terms of Use</Link>
          {/* · <Link to="/imprint">Imprint</Link> */}
        </p>
        <p>© 2026 CARBSE. All rights reserved.</p>
      </div>
    </div>
  );
};

/** React component — Cookies Policy Page. */
export default CookiesPolicyPage;

